import {
  checklistToText,
  textToChecklist,
  type NoteColor,
  type NoteDto,
  type NoteType,
  type UpdateNoteInput,
} from '@keep/shared';
import { newItem, type DraftItem } from './checklist-editor';

/** État éditable d'une note (compositeur ou éditeur). */
export interface NoteDraft {
  title: string;
  content: string;
  type: NoteType;
  items: DraftItem[];
  color: NoteColor;
  labelIds: string[];
  reminderAt: string | null;
  pinned: boolean;
  archived: boolean;
}

export const emptyDraft = (overrides: Partial<NoteDraft> = {}): NoteDraft => ({
  title: '',
  content: '',
  type: 'text',
  items: [],
  color: 'default',
  labelIds: [],
  reminderAt: null,
  pinned: false,
  archived: false,
  ...overrides,
});

export const draftFromNote = (note: NoteDto): NoteDraft => ({
  title: note.title,
  content: note.content,
  type: note.type,
  items: note.items.map((item) => ({ ...item })),
  color: note.color,
  labelIds: [...note.labelIds],
  reminderAt: note.reminderAt,
  pinned: note.pinned,
  archived: note.archived,
});

/** Convertit une modification du brouillon en patch pour l'API. */
export function draftPatch(patch: Partial<NoteDraft>): UpdateNoteInput {
  const result: UpdateNoteInput = { ...patch };
  if (patch.items)
    result.items = patch.items.map(({ id, text, checked }) => ({ id, text, checked }));
  return result;
}

/** « Afficher / Masquer les cases à cocher » : bascule texte <-> liste en conservant le contenu. */
export function toggleChecklist(draft: NoteDraft): Partial<NoteDraft> {
  if (draft.type === 'text') {
    return {
      type: 'checklist',
      items: textToChecklist(draft.content).map((item) => newItem(item.text)),
      content: '',
    };
  }
  return { type: 'text', content: checklistToText(draft.items), items: [] };
}

export function isDraftEmpty(draft: NoteDraft, attachmentCount = 0): boolean {
  return (
    draft.title.trim() === '' &&
    draft.content.trim() === '' &&
    draft.items.every((item) => item.text.trim() === '') &&
    attachmentCount === 0
  );
}
